import { useEffect } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";

import { EntityKind } from "models/entityKind";

import { useFilterScheduledStore } from "./filterScheduledStore";
import { useMapViewFollowUpStore } from "./mapViewFollowUpStore";

const channelName = (channel: string) => `eva-filter-pending:${channel}`;

type FlagsByKind = Record<EntityKind, boolean>;

/** Main tab → popout: the pending-filter flags to mirror. */
interface PendingMessage {
  type: "pending";
  scheduled: FlagsByKind;
  owed: FlagsByKind;
}
/** Popout → main tab: "send me the current flags". */
interface RequestMessage {
  type: "request";
}

const isFlagsByKind = (flags: unknown): flags is FlagsByKind =>
  typeof flags === "object" &&
  flags !== null &&
  typeof (flags as FlagsByKind)[EntityKind.Well] === "boolean" &&
  typeof (flags as FlagsByKind)[EntityKind.Facility] === "boolean";

// Any same-origin sender shares the channel, so only well-formed flags are
// written into the stores.
const isPendingMessage = (data: unknown): data is PendingMessage => {
  if (typeof data !== "object" || data === null) return false;
  const { type, scheduled, owed } = data as Partial<PendingMessage>;
  return type === "pending" && isFlagsByKind(scheduled) && isFlagsByKind(owed);
};

const isRequestMessage = (data: unknown): data is RequestMessage =>
  typeof data === "object" &&
  data !== null &&
  (data as Partial<RequestMessage>).type === "request";

const currentFlags = (): PendingMessage => ({
  type: "pending",
  scheduled: useFilterScheduledStore.getState().scheduled,
  owed: useMapViewFollowUpStore.getState().owed
});

/**
 * Only the main tab runs the filter watcher, so only it knows a filter change
 * is waiting out the debounce or a map-view follow-up run is owed. Redux
 * carries isFiltering and the new filter id to popouts, but these flags are
 * Zustand and do not travel, which left a popped out ML widget free to
 * calculate on a filter about to be replaced (EVA-269). Same
 * publisher/subscriber shape as useTxnIdCrossTabSync (EVA-124).
 */
export function useFilterPendingCrossTabPublisher(): void {
  const channel = useSelector((state: RootState) => state.channel.channel);

  useEffect(() => {
    if (!channel) return;
    const bc = new BroadcastChannel(channelName(channel));
    let closed = false;
    // redux-state-sync's broadcast-channel posts one microtask after dispatch.
    // Posting a microtask later too keeps the two in dispatch order: a run's
    // isFiltering=true reaches the popout before the scheduled flag clears.
    const publish = () =>
      queueMicrotask(() => {
        if (!closed) bc.postMessage(currentFlags());
      });
    const unsubscribeScheduled = useFilterScheduledStore.subscribe((state, prev) => {
      if (state.scheduled !== prev.scheduled) publish();
    });
    const unsubscribeOwed = useMapViewFollowUpStore.subscribe((state, prev) => {
      if (state.owed !== prev.owed) publish();
    });
    bc.onmessage = (event: MessageEvent<unknown>) => {
      if (isRequestMessage(event.data)) publish();
    };
    return () => {
      unsubscribeScheduled();
      unsubscribeOwed();
      closed = true;
      bc.close();
    };
  }, [channel]);
}

/**
 * Mount in the popout dashboard; the popout is a mirror, not a source. Its
 * filter watcher does not run, so nothing there acts on the mirrored debt.
 */
export function useFilterPendingCrossTabSubscriber(): void {
  const channel = useSelector((state: RootState) => state.channel.channel);

  useEffect(() => {
    if (!channel) return;
    const bc = new BroadcastChannel(channelName(channel));
    bc.onmessage = (event: MessageEvent<unknown>) => {
      if (!isPendingMessage(event.data)) return;
      const { scheduled, owed } = event.data;
      const { setFilterScheduled } = useFilterScheduledStore.getState();
      const { setMapViewFollowUpOwed } = useMapViewFollowUpStore.getState();
      for (const kind of [EntityKind.Well, EntityKind.Facility]) {
        setFilterScheduled(kind, scheduled[kind]);
        setMapViewFollowUpOwed(kind, owed[kind]);
      }
    };
    const request: RequestMessage = { type: "request" };
    bc.postMessage(request);
    return () => bc.close();
  }, [channel]);
}
